import { isKeyHotkey, type KeyboardEventLike } from 'is-hotkey';

import { IS_IOS, IS_MAC } from '@gitbook/slate-dev-environment';

const IS_APPLE = IS_IOS || IS_MAC;

/*
 * Hotkeys.
 */

const isBold = isKeyHotkey('mod+b');
const isItalic = isKeyHotkey('mod+i');
const isStrikethrough = isKeyHotkey('mod+shift+s');

const isEscape = isKeyHotkey('escape');
const isEnter = isKeyHotkey('enter');
const isShiftEnter = isKeyHotkey('shift+enter');
const isSplitBlock = (e: KeyboardEventLike) => isEnter(e) || isShiftEnter(e);
const isExitBlockRange = (e: KeyboardEventLike) => isEnter(e) && !isShiftEnter(e);

const isBackspace = isKeyHotkey('backspace');
const isShiftBackspace = isKeyHotkey('shift+backspace');
const isDelete = isKeyHotkey('delete');
const isShiftDelete = isKeyHotkey('shift+delete');
const isDeleteBackward = (e: KeyboardEventLike) => isBackspace(e) || isShiftBackspace(e);
const isDeleteForward = (e: KeyboardEventLike) => isDelete(e) || isShiftDelete(e);

const isDeleteCharBackwardMac = isKeyHotkey('ctrl+h');
const isDeleteCharForwardMac = isKeyHotkey('ctrl+d');
const isDeleteCharBackward = (e: KeyboardEventLike) =>
    isDeleteBackward(e) || (IS_APPLE && isDeleteCharBackwardMac(e));
const isDeleteCharForward = (e: KeyboardEventLike) =>
    isDeleteForward(e) || (IS_APPLE && isDeleteCharForwardMac(e));

const isDeleteLineBackwardMac = (e: KeyboardEventLike) =>
    isKeyHotkey('cmd+shift+backspace', e) || isKeyHotkey('cmd+backspace', e);
const isDeleteLineForwardMac = isKeyHotkey('ctrl+k');
const isDeleteLineBackward = (e: KeyboardEventLike) => IS_APPLE && isDeleteLineBackwardMac(e);
const isDeleteLineForward = (e: KeyboardEventLike) => IS_APPLE && isDeleteLineForwardMac(e);

const isDeleteWordBackwardMac = (e: KeyboardEventLike) =>
    isKeyHotkey('shift+option+backspace', e) || isKeyHotkey('option+backspace', e);
const isDeleteWordBackwardPC = isKeyHotkey('ctrl+backspace');
const isDeleteWordForwardMac = (e: KeyboardEventLike) =>
    isKeyHotkey('shift+option+delete', e) || isKeyHotkey('option+delete', e);
const isDeleteWordForwardPC = isKeyHotkey('ctrl+delete');
const isDeleteWordBackward = (e: KeyboardEventLike) =>
    IS_APPLE ? isDeleteWordBackwardMac(e) : isDeleteWordBackwardPC(e);
const isDeleteWordForward = (e: KeyboardEventLike) =>
    IS_APPLE ? isDeleteWordForwardMac(e) : isDeleteWordForwardPC(e);

const isExtendCharForward = isKeyHotkey('shift+right');
const isExtendCharBackward = isKeyHotkey('shift+left');

const isRightArrow = isKeyHotkey('right');
const isLeftArrow = isKeyHotkey('left');
const isCollapseCharForward = (e: KeyboardEventLike) => isRightArrow(e) && !isExtendCharForward(e);
const isCollapseCharBackward = (e: KeyboardEventLike) => isLeftArrow(e) && !isExtendCharBackward(e);

const isCollapseLineBackwardMac = isKeyHotkey('option+up');
const isCollapseLineForwardMac = isKeyHotkey('option+down');
const isCollapseLineBackward = (e: KeyboardEventLike) => IS_APPLE && isCollapseLineBackwardMac(e);
const isCollapseLineForward = (e: KeyboardEventLike) => IS_APPLE && isCollapseLineForwardMac(e);

const isExtendLineBackwardMac = isKeyHotkey('option+shift+up');
const isExtendLineForwardMac = isKeyHotkey('option+shift+down');
const isExtendLineBackward = (e: KeyboardEventLike) => IS_APPLE && isExtendLineBackwardMac(e);
const isExtendLineForward = (e: KeyboardEventLike) => IS_APPLE && isExtendLineForwardMac(e);

const isUpArrow = isKeyHotkey('up');
const isDownArrow = isKeyHotkey('down');
const isExtendUp = isKeyHotkey('shift+up');
const isExtendDown = isKeyHotkey('shift+down');
const isExtendBlockBackward = (e: KeyboardEventLike) => isExtendUp(e) && !isExtendLineBackward(e);
const isExtendBlockForward = (e: KeyboardEventLike) => isExtendDown(e) && !isExtendLineForward(e);
const isMoveUp = (e: KeyboardEventLike) =>
    isUpArrow(e) && !isExtendLineBackward(e) && !isCollapseLineBackward(e);
const isMoveDown = (e: KeyboardEventLike) =>
    isDownArrow(e) && !isExtendLineForward(e) && !isCollapseLineForward(e);

const isUndo = isKeyHotkey('mod+z');

// mod+shift+z and mod+y are both used for redo (mod+y more common on Windows). Support both (inspired by Notion).
const isRedo = (e: KeyboardEventLike) => isKeyHotkey('mod+shift+z', e) || isKeyHotkey('mod+y', e);

const isRawPaste = isKeyHotkey('mod+shift+v');

const isSelectAll = isKeyHotkey('mod+a');

const isTransposeCharacterMac = isKeyHotkey('ctrl+t');
const isTransposeCharacter = (e: KeyboardEventLike) => IS_APPLE && isTransposeCharacterMac(e);

const isEscapeSelection = (e: KeyboardEventLike) => isEscape(e);

const isDuplicate = isKeyHotkey('mod+d');

const isContentEditable = (e: KeyboardEventLike) =>
    isBold(e) ||
    isDeleteCharBackward(e) ||
    isDeleteCharForward(e) ||
    isDeleteLineBackward(e) ||
    isDeleteLineForward(e) ||
    isDeleteWordBackward(e) ||
    isDeleteWordForward(e) ||
    isItalic(e) ||
    isRedo(e) ||
    isSplitBlock(e) ||
    isTransposeCharacter(e) ||
    isUndo(e) ||
    isStrikethrough(e);

const isComposing = (e: KeyboardEventLike) =>
    e.key === 'ArrowDown' ||
    e.key === 'ArrowLeft' ||
    e.key === 'ArrowRight' ||
    e.key === 'ArrowUp' ||
    e.key === 'Backspace' ||
    e.key === 'Enter';

const Hotkeys = {
    isBold,
    isCollapseCharBackward,
    isCollapseCharForward,
    isCollapseLineBackward,
    isCollapseLineForward,
    isComposing,
    isContentEditable,
    isDeleteCharBackward,
    isDeleteCharForward,
    isDeleteLineBackward,
    isDeleteLineForward,
    isDeleteWordBackward,
    isDeleteWordForward,
    isMoveUp,
    isMoveDown,
    isExtendCharBackward,
    isExtendCharForward,
    isExtendLineBackward,
    isExtendLineForward,
    isExtendBlockBackward,
    isExtendBlockForward,
    isExitBlockRange,
    isItalic,
    isRedo,
    isSplitBlock,
    isUndo,
    isRawPaste,
    isStrikethrough,
    isSelectAll,
    isEscapeSelection,
    isDuplicate,
};

export default Hotkeys;
